import React, { useState } from "react";
import { EConsentMarker } from "@newComponents/eConsent/markers/EConsentMarker";
import { EConsentMarkerLabel } from "@newComponents/eConsent/markers/EConsentMarkerLabel";
import { EConsentUnlinkButton } from "@newComponents/eConsent/markers/EConsentUnlinkButton";
import { MarkerResizer, ResizeDirection } from "@newComponents/eConsent/markers/MarkerResizer";
import { TemplateMarker } from "@newComponents/eConsent/markers/TemplateMarker";
import { useDocumentContext } from "@newHooks/context/document/document.context";
import { DocumentMarker, useDocumentMarkerContext } from "@newHooks/context/document/documentMarker.context";
import { useEConsentContext } from "@newHooks/context/eConsent/eConsent.context";
import { StyledMarkerZone } from "./StyledViewer";
import { DocumentSignerSignatureExt, inputItemCss } from "./types";
import {
  DocumentSignerAttr,
  DocumentSignerAttrTarget,
  DocumentSignerAttrType,
  DocumentSignerInputItemCss,
  DocumentSignType,
} from "@maven-rest/docs/DocumentSign.dto";
import { JsonHelper } from "@maven-helper/helpers/Json.helper";
import { isEqual, throttle } from "lodash";
import { v4 as uuidv4 } from "uuid";

//NOTE: 일정 상 width는 하드코딩으로 정함. 추후 수정 필요
export const nameWidth = 80;
export const signatureWidth = 100;
export const dateWidth = 70;
export const elementHeight = 24;
export const gutter = 8;

export function EConsentDocsPdfViewerPageMarkerZone({
  page,
  zoom,
  width,
  height,
  onMarkerClicked,
  signatures,
  signType,
}: {
  page: number;
  zoom: number;
  width: number;
  height: number;
  onMarkerClicked?: (signerUuid: string, attrUuid: string, attr: DocumentSignerAttrType) => void;
  signatures?: DocumentSignerSignatureExt[];
  signType?: DocumentSignType;
}) {
  const pageIndex = page - 1;
  const { mode, setMode, programmatically } = useDocumentContext();
  const { markers, setMarkers } = useDocumentMarkerContext();
  const { signee, setSignee, investigator, setInvestigator } = useEConsentContext();

  const isDragable = mode === "SIGNER" || (typeof mode === "object" && mode?.type === "LINKING");

  const [currentLinkingAttr, setCurrentLinkingAttr] = useState<{ uuid: string; attr: DocumentSignerAttrType }>();
  const link = (ev: React.MouseEvent<HTMLDivElement, MouseEvent>, page: number) => {
    if (typeof mode !== "object" || mode.type !== "LINKING") return;

    let currentTargetRect = ev.currentTarget.getBoundingClientRect();
    const offsetX = ev.pageX - currentTargetRect.left;
    const offsetY = ev.pageY - window.pageYOffset - currentTargetRect.top;
    const width = currentTargetRect.width;
    const height = currentTargetRect.height;

    const x = Math.min(99, Math.max(0, (offsetX / width) * 100));
    const y = Math.min(99, Math.max(0, (offsetY / height) * 100));

    const newMarker = { ...mode, x, y, page, groupUuid: mode.groupUuid, attrUuid: mode.attrUuid };

    if (mode.attr === DocumentSignerAttrType.TemplateMark) {
      const signer = mode.signerIndex === 1 ? signee : investigator;
      const setSigner = mode.signerIndex === 1 ? setSignee : setInvestigator;
      const templateIndex = signer.attrs.find((x) => x.attr === DocumentSignerAttrType.TemplateIndex && x.attrUuid === mode.groupUuid);
      const templateElements = templateIndex?.value?.split(",") as DocumentSignerAttrType[]; //NOTE: templateIndex value에 현재 템플릿의 구성요소와 순서가 저장되어있음.

      //NOTE: 마커를 찍는 순간 attr을 만들고 이를 바탕으로 마커를 만든다.
      const newAttrs: DocumentSignerAttr[] = templateElements
        .filter((el) => !!el)
        .map((el) => ({
          attr: el,
          attrUuid: uuidv4(),
          groupUuid: mode.attrUuid,
          coordX: null,
          coordY: null,
          pageIndex: null,
          target: DocumentSignerAttrTarget.Signer,
        }));

      setSigner((prev) => {
        return {
          ...prev,
          attrs: prev.attrs.concat(newAttrs),
        };
      });

      const dx1 =
        newAttrs[0].attr === DocumentSignerAttrType.Name
          ? nameWidth
          : newAttrs[0].attr === DocumentSignerAttrType.Signature
          ? signatureWidth
          : dateWidth;
      const dx2 =
        (newAttrs[1].attr === DocumentSignerAttrType.Name
          ? nameWidth
          : newAttrs[1].attr === DocumentSignerAttrType.Signature
          ? signatureWidth
          : dateWidth) + dx1;
      const dx = [0, dx1, dx2].map((el, index) => (el === 0 ? 0 : el + gutter * index)).map((el) => el * zoom);

      const newMarkers: DocumentMarker[] = newAttrs.map(
        (attr, index) =>
          ({
            ...attr,
            signerIndex: mode.signerIndex,
            signerUuid: mode.signerUuid,
            x: dx[index] / zoom + 6,
            y,
            page,
          } as DocumentMarker)
      );

      setMarkers(
        markers
          .filter(
            (x) =>
              !(
                (mode.attrUuid === x.groupUuid &&
                  (x.attr === DocumentSignerAttrType.Name ||
                    x.attr === DocumentSignerAttrType.Signature ||
                    x.attr === DocumentSignerAttrType.Date)) ||
                (mode.attrUuid === x.attrUuid && x.attr === DocumentSignerAttrType.TemplateMark)
              )
          )
          .concat([...newMarkers, newMarker])
      );
    } else {
      setMarkers(
        markers.filter((x) => !(mode.groupUuid === x.groupUuid && x.attrUuid === mode.attrUuid && x.attr === mode.attr)).concat([newMarker])
      );
    }

    setCurrentLinkingAttr({ uuid: mode.signerUuid, attr: mode.attr });
  };

  const endLink = () => {
    if (typeof mode !== "object" || mode.type !== "LINKING") return;

    setCurrentLinkingAttr(undefined);
    setMode("SIGNER");
  };

  const markersFilterCriteria = (marker: DocumentMarker) =>
    marker.page === pageIndex &&
    marker.x !== null &&
    marker.y !== null &&
    marker.attr !== DocumentSignerAttrType.ResponsiveItemValue &&
    marker.attr !== DocumentSignerAttrType.AgentResponsiveItemValue;

  const handleMouseMoveMarkerZone = (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
    !!markers.find((marker) => marker.signerUuid === currentLinkingAttr?.uuid && marker.attr === currentLinkingAttr?.attr) &&
    link(ev, pageIndex);

  const handleClickMarker = (e: React.MouseEvent<HTMLDivElement>, marker: DocumentMarker) => {
    pickResizingMarker(e, marker);
    onMarkerClicked?.(marker.signerUuid, marker.attrUuid, marker.attr);
  };

  const [draggingMarker, setDraggingMarker] = useState<DocumentMarker>();
  const [draggingElement, setDraggingElement] = useState<HTMLDivElement>();
  const [draggingStartPosition, setDraggingStartPosition] = useState({ x: 0, y: 0 });
  const dragStart = (e: React.MouseEvent<HTMLDivElement>, marker: DocumentMarker) => {
    if (!isDragable) return;

    setDraggingMarker(marker);
    setDraggingElement(e.currentTarget);

    const currentTargetRect = e.currentTarget.getBoundingClientRect();
    setDraggingStartPosition({
      x: e.clientX - currentTargetRect.left,
      y: e.clientY - currentTargetRect.top - (currentTargetRect.bottom - currentTargetRect.top) / 2,
    });
  };

  const dragMarker = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!draggingMarker) return;
    if (mode !== "SIGNER") return;
    const currentTargetRect = e.currentTarget.getBoundingClientRect();
    const rectWidth = currentTargetRect.width;
    const height = currentTargetRect.height;

    let newX = 0;
    let newY = 0;

    const parentTemplateMarker = markers.find(
      (marker) => draggingMarker?.groupUuid === marker.attrUuid && marker.attr === DocumentSignerAttrType.TemplateMark
    );

    const isTemplateChildren =
      (draggingMarker.attr === DocumentSignerAttrType.Name ||
        draggingMarker.attr === DocumentSignerAttrType.Signature ||
        draggingMarker.attr === DocumentSignerAttrType.Date) &&
      !!parentTemplateMarker;

    const maxX = ((width - (draggingElement?.getBoundingClientRect().width ?? 0) / zoom) / width) * 99;
    const minY = ((draggingElement?.getBoundingClientRect().height ?? 0) / 2 / height) * 100;
    const maxY = ((height - (draggingElement?.getBoundingClientRect().height ?? 0) / 2) / height) * 99;

    if (isTemplateChildren) {
      const templateX = (parentTemplateMarker.x! / 100) * rectWidth;
      const templateY = (parentTemplateMarker.y! / 100) * height;

      newX = (e.clientX - currentTargetRect.left - draggingStartPosition.x - templateX) / zoom;
      newY = (e.clientY - currentTargetRect.top - draggingStartPosition.y - templateY) / zoom;
    } else {
      newX = Math.min(maxX, Math.max(0, ((e.clientX - currentTargetRect.left - draggingStartPosition.x) / rectWidth) * 100));
      newY = Math.min(maxY, Math.max(minY, ((e.clientY - currentTargetRect.top - draggingStartPosition.y) / height) * 100));
    }

    setMarkers(
      markers
        .filter(
          (x) => !(draggingMarker.groupUuid === x.groupUuid && x.attrUuid === draggingMarker.attrUuid && x.attr === draggingMarker.attr)
        )
        .concat([{ ...draggingMarker, x: newX, y: newY }])
    );

    setDraggingMarker({ ...draggingMarker, x: newX, y: newY });
  };

  const dragEnd = () => {
    const parentTemplateMarker = markers.find(
      (marker) => draggingMarker?.groupUuid === marker.attrUuid && marker.attr === DocumentSignerAttrType.TemplateMark
    );

    const leftestChild = markers.filter((marker) => marker.groupUuid === parentTemplateMarker?.attrUuid).sort((a, b) => a.x! - b.x!)[0];

    if (parentTemplateMarker && leftestChild && leftestChild.x !== 6) {
      const gap = leftestChild.x! - 6;
      const updatedMarkers = markers.map((el) => {
        if (el.attrUuid === parentTemplateMarker?.attrUuid && el.attr === DocumentSignerAttrType.TemplateMark) {
          return {
            ...el,
            x: Math.min(99, Math.max(0, el.x! + (gap / 595) * 100)),
          };
        } else if (el.groupUuid === parentTemplateMarker?.attrUuid) {
          return {
            ...el,
            x: el.x! - gap,
          };
        } else {
          return el;
        }
      });

      setMarkers(updatedMarkers);
    }

    setDraggingMarker(undefined);
  };
  const [resizingMarker, setResizingMarker] = useState<DocumentMarker>();

  const pickResizingMarker = (e: React.MouseEvent<HTMLDivElement>, marker: DocumentMarker) => {
    if (mode !== "SIGNER") return;
    setResizingMarker(marker);
  };

  const resizeMarker = throttle((e: React.MouseEvent<HTMLDivElement>) => {
    if (!resizingMarker) return;
    if (mode !== "SIGNER") return;

    const inputItemCssAttr = signee.attrs.find(
      (el) =>
        el.groupUuid === resizingMarker.groupUuid &&
        (el.attr === DocumentSignerAttrType.InputItemCss || el.attr === DocumentSignerAttrType.AgentInputItemCss)
    );

    const inputItemCssValue = JsonHelper.parse(inputItemCssAttr?.value ?? "{}") as DocumentSignerInputItemCss;

    const direction = resizeDirection?.toString();
    const dx = ((direction?.includes("LEFT") ? -1 : direction?.includes("RIGHT") ? 1 : 0) * e.movementX) / zoom;
    const dy = (((direction?.includes("TOP") ? -1 : direction?.includes("BOTTOM") ? 1 : 0) * e.movementY) / zoom) * 2; //NOTE: transform: translateY(-50%) 때문에 2배로 보정

    const newWidth = Math.min(Math.max(22, (inputItemCssValue.width ?? inputItemCss.width!) + dx), 480);
    const newHeight = Math.min(Math.max(22, (inputItemCssValue.height ?? inputItemCss.height!) + dy), 400);

    const newInputItemCssValue: DocumentSignerInputItemCss = { ...inputItemCssValue, width: newWidth, height: newHeight };
    const newInputItemCss = {
      target: DocumentSignerAttrTarget.Signer,
      attr: resizingMarker.attr.includes("AGENT") ? DocumentSignerAttrType.AgentInputItemCss : DocumentSignerAttrType.InputItemCss,
      value: JsonHelper.stringify(newInputItemCssValue),
      groupUuid: resizingMarker.groupUuid,
      attrUuid: uuidv4(),
      pageIndex: null,
      coordX: null,
      coordY: null,
    };

    setSignee((prev) => {
      return {
        ...prev,
        attrs: !inputItemCssAttr
          ? [...prev.attrs, newInputItemCss]
          : prev.attrs.map((el) =>
              el.attr === inputItemCssAttr.attr && el.groupUuid === inputItemCssAttr.groupUuid && el.attrUuid === inputItemCssAttr.attrUuid
                ? { ...el, value: JsonHelper.stringify(newInputItemCssValue) }
                : el
            ),
      };
    });
  }, 100);

  const resizeEnd = () => {
    setResizingMarker(undefined);
    setResizeDirection(undefined);
  };

  const isModeResizing = (marker: DocumentMarker) => isEqual(marker, resizingMarker);
  const [resizeDirection, setResizeDirection] = useState<keyof ResizeDirection>();

  return (
    <StyledMarkerZone
      width={`calc(${width}px - 2px)`}
      height={`calc(${height}px - 2px)`}
      transform={`scale(${zoom})`}
      transformOrigin="top left"
      onMouseDown={(ev) => link(ev, pageIndex)}
      onMouseMove={(ev) => {
        if (resizingMarker && resizeDirection) {
          resizeMarker(ev);
        } else {
          dragMarker(ev);
        }
        handleMouseMoveMarkerZone(ev);
      }}
      onMouseUp={() => {
        endLink();
        dragEnd();
        resizeEnd();
      }}
      isVisible
      isModeLinking={typeof mode !== "string" && "type" in mode && mode.type === "LINKING"}
    >
      {markers
        .reduce(
          ([pass, fail], marker) => {
            return isTemplateOrTemplateChild(marker, markers) ? [[...pass, marker], fail] : [pass, [...fail, marker]];
          },
          [[], []] as DocumentMarker[][]
        )[0]
        .filter((el) => el.attr === DocumentSignerAttrType.TemplateMark)
        .filter(markersFilterCriteria)
        .map((TemplateMarker) => {
          const children = markers.filter(
            (x) =>
              x.groupUuid === TemplateMarker.attrUuid &&
              (x.attr === DocumentSignerAttrType.Name ||
                x.attr === DocumentSignerAttrType.Signature ||
                x.attr === DocumentSignerAttrType.Date)
          );

          return {
            parent: TemplateMarker,
            children,
          };
        })
        .map((template) => (
          <TemplateMarker
            id={
              template.parent.id
                ? `mdx--pdf-marker--id-${template.parent.id}`
                : `mdx--pdf-marker--uuid-${template.parent.groupUuid}-${template.parent.attrUuid}`
            }
            key={`${template.parent.signerUuid}-${template.parent.attr}-${template.parent.groupUuid || "NONE"}-${
              template.parent.attrUuid || "NONE"
            }`}
            top={`${template.parent.y}%`}
            left={`${template.parent.x}%`}
            isModeLinking={typeof mode !== "string" && "type" in mode && mode.type === "LINKING"}
            isModeSigning={mode === "SIGNING"}
            isModeSigner={mode === "SIGNER"}
            isReadonly={programmatically}
            marker={template.parent}
            isHidden={
              mode === "SIGNING" &&
              (markers.find((e) => e.attr === DocumentSignerAttrType.AgentAvailableMark)?.value === "true"
                ? template.parent.attr === DocumentSignerAttrType.Signature
                : template.parent.attr !== DocumentSignerAttrType.AgentAvailableMark && template.parent.attr.toString().includes("AGENT"))
            }
            signType={signType}
            signatures={signatures}
            onClick={(e) => handleClickMarker(e, template.parent)}
            onMouseDown={(e) => {
              dragStart(e, template.parent);
            }}
            resizeMode={isModeResizing(template.parent)}
            templateChildren={template.children}
            isModeDragging={!!draggingMarker}
          >
            {template.children.map((marker) => {
              return (
                <>
                  <EConsentMarker
                    id={marker.id ? `mdx--pdf-marker--id-${marker.id}` : `mdx--pdf-marker--uuid-${marker.groupUuid}-${marker.attrUuid}`}
                    key={`${marker.signerUuid}-${marker.attr}-${marker.groupUuid || "NONE"}-${marker.attrUuid || "NONE"}`}
                    top={"50%"}
                    left={`${marker.x}px`}
                    isModeLinking={typeof mode !== "string" && "type" in mode && mode.type === "LINKING"}
                    isModeSigning={mode === "SIGNING"}
                    isModeSigner={mode === "SIGNER"}
                    isReadonly={programmatically}
                    marker={marker}
                    isHidden={
                      mode === "SIGNING" &&
                      (markers.find((e) => e.attr === DocumentSignerAttrType.AgentAvailableMark)?.value === "true"
                        ? marker.attr === DocumentSignerAttrType.Signature
                        : marker.attr !== DocumentSignerAttrType.AgentAvailableMark && marker.attr.toString().includes("AGENT"))
                    }
                    signType={signType}
                    signatures={signatures}
                    onClick={(e) => {
                      handleClickMarker(e, marker);
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      dragStart(e, marker);
                    }}
                    resizeMode={isModeResizing(marker)}
                  >
                    {isModeResizing(marker) &&
                      (marker.attr === DocumentSignerAttrType.InputItemMark ||
                        marker.attr === DocumentSignerAttrType.AgentInputItemMark) && (
                        <MarkerResizer onMouseDown={(direction) => setResizeDirection(direction)} />
                      )}
                    {mode !== "SIGNING" && <EConsentMarkerLabel marker={marker} />}
                    {mode === "SIGNER" && !programmatically && <EConsentUnlinkButton marker={marker} />}
                  </EConsentMarker>
                </>
              );
            })}
            {mode === "SIGNER" && !programmatically && <EConsentUnlinkButton marker={template.parent} />}
          </TemplateMarker>
        ))}
      {markers
        .reduce(
          ([pass, fail], marker) => {
            return isTemplateOrTemplateChild(marker, markers) ? [[...pass, marker], fail] : [pass, [...fail, marker]];
          },
          [[], []] as DocumentMarker[][]
        )[1]
        .filter(markersFilterCriteria)
        .map((marker) => {
          return (
            <EConsentMarker
              id={marker.id ? `mdx--pdf-marker--id-${marker.id}` : `mdx--pdf-marker--uuid-${marker.groupUuid}-${marker.attrUuid}`}
              key={`${marker.signerUuid}-${marker.attr}-${marker.groupUuid || "NONE"}-${marker.attrUuid || "NONE"}`}
              top={`${marker.y}%`}
              left={`${marker.x}%`}
              isModeLinking={typeof mode !== "string" && "type" in mode && mode.type === "LINKING"}
              isModeSigning={mode === "SIGNING"}
              isModeSigner={mode === "SIGNER"}
              isReadonly={programmatically}
              marker={marker}
              isHidden={
                mode === "SIGNING" &&
                (markers.find((e) => e.attr === DocumentSignerAttrType.AgentAvailableMark)?.value === "true"
                  ? marker.attr === DocumentSignerAttrType.Signature
                  : marker.attr !== DocumentSignerAttrType.AgentAvailableMark && marker.attr.toString().includes("AGENT"))
              }
              signType={signType}
              signatures={signatures}
              onClick={(e) => {
                handleClickMarker(e, marker);
              }}
              onMouseDown={(e) => {
                dragStart(e, marker);
              }}
              resizeMode={isModeResizing(marker)}
            >
              {isModeResizing(marker) &&
                (marker.attr === DocumentSignerAttrType.InputItemMark || marker.attr === DocumentSignerAttrType.AgentInputItemMark) && (
                  <MarkerResizer onMouseDown={(direction) => setResizeDirection(direction)} />
                )}
              {mode !== "SIGNING" && <EConsentMarkerLabel marker={marker} />}
              {mode === "SIGNER" && !resizingMarker && !programmatically && <EConsentUnlinkButton marker={marker} />}
            </EConsentMarker>
          );
        })}
    </StyledMarkerZone>
  );
}

function isTemplateOrTemplateChild(marker: DocumentMarker, markers: DocumentMarker[]) {
  const isTemplate = marker.attr === DocumentSignerAttrType.TemplateMark;

  const isNameChild =
    marker.attr === DocumentSignerAttrType.Name &&
    markers.find((x) => x.attrUuid === marker.groupUuid && x.attr === DocumentSignerAttrType.TemplateMark);
  const isSignatureChild =
    marker.attr === DocumentSignerAttrType.Signature &&
    markers.find((x) => x.attrUuid === marker.groupUuid && x.attr === DocumentSignerAttrType.TemplateMark);
  const isDateChild =
    marker.attr === DocumentSignerAttrType.Date &&
    markers.find((x) => x.attrUuid === marker.groupUuid && x.attr === DocumentSignerAttrType.TemplateMark);

  return !!isTemplate || !!isNameChild || !!isSignatureChild || !!isDateChild;
}
